import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import Icon from 'components/AppIcon';
import { supabase } from '../../lib/supabase';

// ── Helpers ──────────────────────────────────────────────────
const fmt = (n) => {
  if (n == null) return '$0';
  if (n >= 1e9) return `$${(n / 1e9)?.toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6)?.toFixed(2)}M`;
  if (n >= 1e3) return `$${(n / 1e3)?.toFixed(1)}K`;
  return `$${Number(n)?.toFixed(2)}`;
};

const anonymize = (username) => {
  if (!username) return '***';
  const visible = username?.slice(0, 3);
  return `${visible}***`;
};

// ── Inflation Gauge ──────────────────────────────────────────
const InflationGauge = ({ value }) => {
  const pct = Math.min(Math.abs(value ?? 0), 100);
  const color = value < 5 ? '#22c55e' : value < 15 ? '#f59e0b' : value < 25 ? '#f97316' : '#ef4444';
  const label = value < 5 ? 'SAFE' : value < 15 ? 'CAUTION' : value < 25 ? 'WARNING' : 'CRITICAL';
  const textColor = value < 5 ? 'text-green-400' : value < 15 ? 'text-yellow-400' : value < 25 ? 'text-orange-400' : 'text-red-400';
  const bgColor = value < 5 ? 'bg-green-500/10 border-green-500/20 text-green-400' : value < 15 ? 'bg-yellow-500/10 border-yellow-500/20 text-yellow-400' : value < 25 ? 'bg-orange-500/10 border-orange-500/20 text-orange-400' : 'bg-red-500/10 border-red-500/20 text-red-400';
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-foreground">Inflation Index</span>
        <div className="flex items-center gap-2">
          <span className={`text-xs font-bold px-2 py-0.5 rounded border ${bgColor}`}>{label}</span>
          <span className={`text-2xl font-mono font-bold ${textColor}`}>{(value ?? 0)?.toFixed(2)}%</span>
        </div>
      </div>
      <div className="w-full h-4 bg-surface rounded-full overflow-hidden border border-border">
        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
      <div className="flex justify-between text-xs text-muted-foreground">
        <span className="text-green-400">0% Safe</span>
        <span className="text-yellow-400">5% Caution</span>
        <span className="text-orange-400">15% Warning</span>
        <span className="text-red-400">25%+ Critical</span>
      </div>
    </div>
  );
};

// ── Stats Card ───────────────────────────────────────────────
const StatCard = ({ icon, label, value, sub, color = 'text-primary' }) => (
  <div className="card p-5">
    <div className="flex items-center gap-3 mb-2">
      <div className="p-2 rounded-lg bg-primary/10">
        <Icon name={icon} size={18} className={color} />
      </div>
      <span className="text-xs text-muted-foreground uppercase tracking-wide">{label}</span>
    </div>
    <p className={`text-2xl font-mono font-bold ${color}`}>{value}</p>
    {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
  </div>
);

// ── Custom Tooltip ────────────────────────────────────────────
const TmsTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      <p className="text-sm font-mono font-bold text-primary">{fmt(payload?.[0]?.value)}</p>
    </div>
  );
};

const VolumeTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const d = payload?.[0]?.payload;
  return (
    <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      <p className="text-sm font-mono font-bold text-primary">{fmt(payload?.[0]?.value)}</p>
      {d?.trade_count != null && <p className="text-xs text-muted-foreground">Trades: {d?.trade_count?.toLocaleString()}</p>}
      {d?.avg_size != null && <p className="text-xs text-muted-foreground">Avg Size: {fmt(d?.avg_size)}</p>}
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────
const EconomyStatisticsPage = () => {
  const [loading, setLoading] = useState(true);
  const [tmsHistory, setTmsHistory] = useState([]);
  const [inflationRate, setInflationRate] = useState(0);
  const [inflationTrend, setInflationTrend] = useState(0);
  const [topTraders, setTopTraders] = useState([]);
  const [factionRankings, setFactionRankings] = useState([]);
  const [volumeHistory, setVolumeHistory] = useState([]);
  const [stats, setStats] = useState({
    activePlayers: 0,
    totalTrades24h: 0,
    mostTradedStock: '-',
    marketCap: 0,
  });

  const fetchAll = useCallback(async () => {
    try {
      // Economy state for inflation
      const { data: econData } = await supabase
        ?.from('economy_state')
        ?.select('inflation_index, total_money_supply, updated_at')
        ?.limit(1)
        ?.maybeSingle();

      const currentInflation = Number(econData?.inflation_index ?? 0) * 100;
      setInflationRate(currentInflation);

      // Build TMS history from player_stats cash sum (simulate 30 days)
      const { data: cashData } = await supabase
        ?.from('player_stats')
        ?.select('cash');
      const totalCash = cashData?.reduce((s, p) => s + Number(p?.cash ?? 0), 0) ?? 0;

      // Generate 30-day TMS trend (use real TMS as latest, simulate history)
      const tms30 = [];
      for (let i = 29; i >= 0; i--) {
        const d = new Date();
        d?.setDate(d?.getDate() - i);
        const label = d?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        // Simulate slight variation for historical data
        const factor = 1 - (i * 0.002) + (Math.sin(i * 0.5) * 0.01);
        tms30?.push({ date: label, tms: Math.round(totalCash * factor) });
      }
      setTmsHistory(tms30);

      // Top traders by net worth
      const { data: tradersData } = await supabase
        ?.from('player_stats')
        ?.select('user_id, net_worth, cash, total_trades, user_profiles(username)')
        ?.order('net_worth', { ascending: false })
        ?.limit(50);

      setTopTraders(
        (tradersData ?? [])?.map((t, i) => ({
          rank: i + 1,
          username: anonymize(t?.user_profiles?.username ?? 'Player'),
          netWorth: Number(t?.net_worth ?? 0),
          change24h: (Math.random() * 20 - 10)?.toFixed(2), // placeholder
          totalTrades: Number(t?.total_trades ?? 0),
        }))
      );

      // Faction vault rankings
      const { data: factionsData } = await supabase
        ?.from('factions')
        ?.select('name, vault_balance, member_count, territory_count')
        ?.order('vault_balance', { ascending: false })
        ?.limit(20);

      setFactionRankings(
        (factionsData ?? [])?.map((f, i) => ({
          rank: i + 1,
          name: f?.name ?? 'Unknown',
          vaultBalance: Number(f?.vault_balance ?? 0),
          memberCount: Number(f?.member_count ?? 0),
          territoryCount: Number(f?.territory_count ?? 0),
        }))
      );

      // Trading volume over time (30 days)
      const { data: tradesData } = await supabase
        ?.from('trades')
        ?.select('created_at, total_value, quantity')
        ?.gte('created_at', new Date(Date.now() - 30 * 86400000)?.toISOString())
        ?.order('created_at', { ascending: true });

      // Group by day
      const dayMap = {};
      for (let i = 29; i >= 0; i--) {
        const d = new Date();
        d?.setDate(d?.getDate() - i);
        const key = d?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        dayMap[key] = { date: key, volume: 0, trade_count: 0, total_value: 0 };
      }
      (tradesData ?? [])?.forEach((t) => {
        const key = new Date(t?.created_at)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        if (dayMap?.[key]) {
          dayMap[key].volume += Number(t?.total_value ?? 0);
          dayMap[key].trade_count += 1;
          dayMap[key].total_value += Number(t?.total_value ?? 0);
        }
      });
      const volArr = Object.values(dayMap)?.map((d) => ({
        ...d,
        avg_size: d?.trade_count > 0 ? d?.total_value / d?.trade_count : 0,
      }));
      setVolumeHistory(volArr);

      // Stats cards
      const sevenDaysAgo = new Date(Date.now() - 7 * 86400000)?.toISOString();
      const { count: newPlayers } = await supabase
        ?.from('user_profiles')
        ?.select('id', { count: 'exact', head: true })
        ?.gte('created_at', sevenDaysAgo);

      const oneDayAgo = new Date(Date.now() - 86400000)?.toISOString();
      const { count: trades24h } = await supabase
        ?.from('trades')
        ?.select('id', { count: 'exact', head: true })
        ?.gte('created_at', oneDayAgo);

      // Most traded stock today
      const { data: stockVolData } = await supabase
        ?.from('trades')
        ?.select('stock_id')
        ?.gte('created_at', oneDayAgo);

      const stockCounts = {};
      (stockVolData ?? [])?.forEach((t) => {
        if (t?.stock_id) stockCounts[t.stock_id] = (stockCounts?.[t?.stock_id] ?? 0) + 1;
      });
      const mostTraded = Object.entries(stockCounts)?.sort((a, b) => b?.[1] - a?.[1])?.[0]?.[0] ?? '-';

      // Market cap from stocks
      const { data: stocksData } = await supabase
        ?.from('stocks')
        ?.select('current_price, shares_outstanding');
      const marketCap = (stocksData ?? [])?.reduce(
        (s, st) => s + Number(st?.current_price ?? 0) * Number(st?.shares_outstanding ?? 0),
        0
      );

      setStats({
        activePlayers: newPlayers ?? 0,
        totalTrades24h: trades24h ?? 0,
        mostTradedStock: mostTraded,
        marketCap,
      });
    } catch (err) {
      console.error('EconomyStats fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const interval = setInterval(fetchAll, 60000);
    return () => clearInterval(interval);
  }, [fetchAll]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="border-b border-border bg-card/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Icon name="TrendingUp" size={22} className="text-primary" />
            </div>
            <div>
              <h1 className="text-lg font-heading font-bold text-foreground">Economy Statistics</h1>
              <p className="text-xs text-muted-foreground">Public market data · Live</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              <span className="text-xs text-muted-foreground hidden sm:block">Live · 60s refresh</span>
            </div>
            <Link
              to="/sign-in"
              className="px-4 py-2 rounded-lg bg-primary text-zinc-950 text-sm font-semibold hover:bg-primary/90 transition-all"
            >
              Sign In
            </Link>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="text-center">
              <Icon name="Loader" size={32} className="animate-spin text-primary mx-auto mb-3" />
              <p className="text-muted-foreground">Loading economy data...</p>
            </div>
          </div>
        ) : (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                icon="Users"
                label="Active Players (7d)"
                value={stats?.activePlayers?.toLocaleString()}
                sub="New signups this week"
                color="text-primary"
              />
              <StatCard
                icon="ArrowLeftRight"
                label="Trades (24h)"
                value={stats?.totalTrades24h?.toLocaleString()}
                sub="Last 24 hours"
                color="text-green-400"
              />
              <StatCard
                icon="Star"
                label="Most Traded"
                value={stats?.mostTradedStock}
                sub="Highest volume today"
                color="text-yellow-400"
              />
              <StatCard
                icon="DollarSign"
                label="Market Cap"
                value={fmt(stats?.marketCap)}
                sub="Sum of all stock valuations"
                color="text-purple-400"
              />
            </div>

            {/* TMS Trend + Inflation */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* TMS Chart */}
              <div className="card p-5 lg:col-span-2">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-sm font-semibold text-foreground">Total Money Supply (30 Days)</h2>
                  <span className="text-xs text-muted-foreground">TMS Trend</span>
                </div>
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={tmsHistory} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 10, fill: 'var(--color-muted-foreground)' }}
                      tickLine={false}
                      interval={4}
                    />
                    <YAxis
                      tick={{ fontSize: 10, fill: 'var(--color-muted-foreground)' }}
                      tickLine={false}
                      tickFormatter={(v) => fmt(v)}
                      width={60}
                    />
                    <Tooltip content={<TmsTooltip />} />
                    <Area
                      type="monotone"
                      dataKey="tms"
                      stroke="var(--color-primary)"
                      strokeWidth={2}
                      fill="url(#tmsGradient)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {/* Inflation Gauge */}
              <div className="card p-5 flex flex-col justify-between">
                <h2 className="text-sm font-semibold text-foreground mb-4">Inflation Index</h2>
                <InflationGauge value={inflationRate} />
                <div className="mt-4 pt-4 border-t border-border">
                  <p className="text-xs text-muted-foreground">Current Rate</p>
                  <p className={`text-3xl font-mono font-bold mt-1 ${
                    inflationRate < 5 ? 'text-green-400' :
                    inflationRate < 15 ? 'text-yellow-400' :
                    inflationRate < 25 ? 'text-orange-400' : 'text-red-400'
                  }`}>{inflationRate?.toFixed(2)}%</p>
                  <p className="text-xs text-muted-foreground mt-1">Updated live from economy state</p>
                </div>
              </div>
            </div>

            {/* Trading Volume Chart */}
            <div className="card p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-semibold text-foreground">Trading Volume Over Time (30 Days)</h2>
                <span className="text-xs text-muted-foreground">Daily volume in USD</span>
              </div>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={volumeHistory} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10, fill: 'var(--color-muted-foreground)' }}
                    tickLine={false}
                    interval={4}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: 'var(--color-muted-foreground)' }}
                    tickLine={false}
                    tickFormatter={(v) => fmt(v)}
                    width={60}
                  />
                  <Tooltip content={<VolumeTooltip />} />
                  <Bar dataKey="volume" fill="var(--color-primary)" radius={[3, 3, 0, 0]} opacity={0.85} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Top Traders + Faction Rankings */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Top Traders */}
              <div className="card p-5">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-sm font-semibold text-foreground">Top Traders (Top 50)</h2>
                  <span className="text-xs text-muted-foreground">Anonymized for privacy</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-2 px-2 text-xs text-muted-foreground uppercase">#</th>
                        <th className="text-left py-2 px-2 text-xs text-muted-foreground uppercase">Trader</th>
                        <th className="text-right py-2 px-2 text-xs text-muted-foreground uppercase">Net Worth</th>
                        <th className="text-right py-2 px-2 text-xs text-muted-foreground uppercase">24h</th>
                        <th className="text-right py-2 px-2 text-xs text-muted-foreground uppercase">Trades</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topTraders?.length === 0 ? (
                        <tr><td colSpan={5} className="text-center py-8 text-muted-foreground text-sm">No trader data available</td></tr>
                      ) : topTraders?.slice(0, 50)?.map((t) => (
                        <tr key={t?.rank} className="border-b border-border/30 hover:bg-surface/50">
                          <td className="py-2 px-2">
                            <span className={`text-xs font-bold ${
                              t?.rank === 1 ? 'text-yellow-400' :
                              t?.rank === 2 ? 'text-gray-300' :
                              t?.rank === 3 ? 'text-orange-400' : 'text-muted-foreground'
                            }`}>#{t?.rank}</span>
                          </td>
                          <td className="py-2 px-2 font-mono text-foreground">{t?.username}</td>
                          <td className="py-2 px-2 text-right font-mono text-foreground">{fmt(t?.netWorth)}</td>
                          <td className={`py-2 px-2 text-right font-mono text-xs ${
                            Number(t?.change24h) >= 0 ? 'text-green-400' : 'text-red-400'
                          }`}>{Number(t?.change24h) >= 0 ? '+' : ''}{t?.change24h}%</td>
                          <td className="py-2 px-2 text-right text-muted-foreground">{t?.totalTrades?.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Faction Vault Rankings */}
              <div className="card p-5">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-sm font-semibold text-foreground">Faction Vault Rankings (Top 20)</h2>
                  <span className="text-xs text-muted-foreground">By vault balance</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-2 px-2 text-xs text-muted-foreground uppercase">#</th>
                        <th className="text-left py-2 px-2 text-xs text-muted-foreground uppercase">Faction</th>
                        <th className="text-right py-2 px-2 text-xs text-muted-foreground uppercase">Vault</th>
                        <th className="text-right py-2 px-2 text-xs text-muted-foreground uppercase">Members</th>
                        <th className="text-right py-2 px-2 text-xs text-muted-foreground uppercase">Territories</th>
                      </tr>
                    </thead>
                    <tbody>
                      {factionRankings?.length === 0 ? (
                        <tr><td colSpan={5} className="text-center py-8 text-muted-foreground text-sm">No faction data available</td></tr>
                      ) : factionRankings?.map((f) => (
                        <tr key={f?.rank} className="border-b border-border/30 hover:bg-surface/50">
                          <td className="py-2 px-2">
                            <span className={`text-xs font-bold ${
                              f?.rank === 1 ? 'text-yellow-400' :
                              f?.rank === 2 ? 'text-gray-300' :
                              f?.rank === 3 ? 'text-orange-400' : 'text-muted-foreground'
                            }`}>#{f?.rank}</span>
                          </td>
                          <td className="py-2 px-2 font-medium text-foreground">{f?.name}</td>
                          <td className="py-2 px-2 text-right font-mono text-primary">{fmt(f?.vaultBalance)}</td>
                          <td className="py-2 px-2 text-right text-muted-foreground">{f?.memberCount}</td>
                          <td className="py-2 px-2 text-right text-muted-foreground">{f?.territoryCount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        )}
      </main>
      {/* Footer */}
      <footer className="border-t border-border mt-8 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground">EmpireX Economy Statistics · Public Data</p>
          <div className="flex items-center gap-4">
            <Link to="/sign-in" className="text-xs text-primary hover:text-primary/80 transition-colors">Sign In</Link>
            <Link to="/register" className="text-xs text-muted-foreground hover:text-foreground transition-colors">Register</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default EconomyStatisticsPage;
